import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Webhook, Download, Copy, Check, FileCode2, Loader2, AlertTriangle,
  ArrowRight, ExternalLink
} from 'lucide-react';

const REF_PATH = '/reference/PayeeRequests.java';

const HOOKS = {
  'party-lookup': {
    label: 'Party lookup',
    when: 'Hub asks: does this DFSP own this customer?',
    response: 'Return 200 with the party, or 4xx if not found.',
    request: `{
  "requestId": "req-abc-123",
  "partyIdType": "MSISDN",
  "partyIdValue": "250788123456",
  "currency": "RWF",
  "requesterFspId": "yourbank"
}`,
    reply: `{
  "partyName": "Jean Damascene",
  "firstName": "Jean",
  "lastName": "Damascene",
  "dateOfBirth": "1980-07-15"
}`,
  },
  'quote': {
    label: 'Quote',
    when: 'Hub asks you (as payee) to price the transfer.',
    response: 'Return your fee, the net receive amount, and an expiry.',
    request: `{
  "requestId": "...",
  "quoteId": "3d492671-…",
  "endToEndId": "E2E-2026-001",
  "amount": 10000.00,
  "currency": "RWF",
  "amountType": "SEND",
  "payer": { "partyIdType": "MSISDN", "partyIdValue": "250788000001", "fspId": "yourbank" },
  "payee": { "partyIdType": "MSISDN", "partyIdValue": "250788123456", "fspId": "bankofkigali" }
}`,
    reply: `{
  "payeeReceiveAmount": 9950.00,
  "payeeFspFee": 50.00,
  "expiration": "2026-02-27T10:30:00Z"
}`,
  },
  'transfer': {
    label: 'Transfer',
    when: 'Hub instructs you to credit the payee and settle.',
    response: 'Credit the account, then return the fulfilment that satisfies the condition.',
    request: `{
  "requestId": "...",
  "transferId": "f1e2d3c4-…",
  "quoteId": "3d492671-…",
  "amount": 10000.00,
  "currency": "RWF",
  "payerFspId": "yourbank",
  "payeeFspId": "bankofkigali",
  "ilpPacket": "AYIBgQAAAAAAAASwNgsBk…",
  "condition": "HOr22-H3rudl6cg…",
  "expiration": "2026-02-27T10:30:00Z"
}`,
    reply: `{
  "fulfilment": "mhPut9r7…"
}`,
  },
  'transfer-error': {
    label: 'Transfer error',
    when: 'Hub notifies that a transfer was aborted.',
    response: 'Fire-and-forget — acknowledge with 200. No body expected.',
    request: `{
  "requestId": "...",
  "transferId": "f1e2d3c4-…",
  "errorCode": "5101",
  "errorDescription": "Payee transaction limit reached",
  "payerFspId": "yourbank",
  "payeeFspId": "bankofkigali"
}`,
    reply: null,
  },
};

const HOOK_ORDER = ['party-lookup', 'quote', 'transfer', 'transfer-error'];

const PITFALLS = [
  ['Respond fast', 'The payer DFSP is blocked, waiting synchronously for this whole chain. A slow webhook causes the payer-side await() to time out even though you eventually answer.'],
  ['Be idempotent', 'The hub retries. Key on requestId / transferId and return the same result for a repeat rather than double-crediting.'],
  ['Return a fulfilment that matches', 'The transfer reply must carry a fulfilment whose SHA-256 equals the condition. The connector verifies before committing — a mismatch aborts the transfer.'],
  ['Reject unknown parties cleanly', 'For a party lookup you do not own, return a 4xx — never fabricate a party. The hub will route elsewhere.'],
  ['Honour the quote expiry', 'A transfer arriving after the quote expiration must be rejected even if everything else is valid.'],
];

export default function CbsWebhookGuide() {
  const [code, setCode]     = useState('');
  const [loading, setLoad]  = useState(true);
  const [error, setError]   = useState(null);
  const [copied, setCopied] = useState(false);
  const [hook, setHook]     = useState('party-lookup');

  useEffect(() => {
    fetch(REF_PATH)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.text(); })
      .then(t => { setCode(t); setLoad(false); })
      .catch(e => { setError(e.message); setLoad(false); });
  }, []);

  const copyCode = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const h = HOOKS[hook];

  return (
    <div className="space-y-8 max-w-4xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Webhook className="w-6 h-6 text-primary-600" />
          CBS Payee Webhook
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          The inbound side of integration. When the hub routes a payment to you <em>as the payee</em>,
          the connector simplifies the FSPIOP payload and POSTs to your CBS webhook. These are the
          four endpoints your core banking system implements.
        </p>
      </div>

      {/* Direction diagram */}
      <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
        <svg viewBox="0 0 680 70" width="100%" role="img"
             aria-label="Mojaloop Hub to connector to your CBS webhook">
          {[['Mojaloop Hub', 90], ['Connector', 340], ['Your CBS webhook', 590]].map(([n, x]) => (
            <g key={n}>
              <rect x={x - 80} y="18" width="160" height="34" rx="8" fill="#fff" stroke="#cbd5e1" strokeWidth="1" />
              <text x={x} y="40" textAnchor="middle" fontSize="13" fontFamily="system-ui" fill="#0f172a">{n}</text>
            </g>
          ))}
          {[[170, 260], [420, 510]].map(([x1, x2], i) => (
            <g key={i}>
              <line x1={x1} y1="35" x2={x2 - 7} y2="35" stroke="#2563eb" strokeWidth="1.5" />
              <path d={`M${x2},35 L${x2 - 7},31 L${x2 - 7},39 Z`} fill="#2563eb" />
            </g>
          ))}
          <text x="340" y="14" textAnchor="middle" fontSize="11" fontFamily="ui-monospace, monospace" fill="#64748b">POST (simplified JSON) →</text>
        </svg>
      </div>

      {/* Webhook selector */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">The four webhooks</h2>
        <div className="flex flex-wrap gap-2 mb-4">
          {HOOK_ORDER.map(k => (
            <button key={k} onClick={() => setHook(k)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                k === hook ? 'bg-primary-600 text-white border-primary-600'
                           : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}>
              {HOOKS[k].label}
            </button>
          ))}
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-sm text-gray-700"><span className="font-medium">When:</span> {h.when}</p>
          <p className="text-sm text-gray-700 mt-1"><span className="font-medium">You respond:</span> {h.response}</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Connector → your webhook</p>
              <pre className="bg-gray-900 text-gray-100 text-xs rounded-lg p-3 overflow-x-auto">{h.request}</pre>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Your webhook → connector</p>
              {h.reply
                ? <pre className="bg-gray-900 text-gray-100 text-xs rounded-lg p-3 overflow-x-auto">{h.reply}</pre>
                : <div className="bg-gray-50 border border-dashed border-gray-200 rounded-lg p-3 text-xs text-gray-500">
                    200 OK, no body — fire-and-forget.
                  </div>}
            </div>
          </div>

          {hook === 'transfer' && (
            <Link to="/guides/ilp"
              className="inline-flex items-center gap-1 mt-3 text-xs px-2 py-1 rounded-md bg-primary-50 text-primary-700 hover:bg-primary-100">
              How the fulfilment satisfies the condition <ExternalLink className="w-3 h-3" />
            </Link>
          )}
        </div>
      </section>

      {/* Config note */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Configuration</h2>
        <p className="text-sm text-gray-600">
          Register your webhook base URL with the connector (per-tenant CBS config, or the
          <span className="font-mono text-xs"> CBS_PAYEE_WEBHOOK_URL</span> env var). The connector
          authenticates to your webhook with a shared key
          (<span className="font-mono text-xs">CBS_PAYEE_WEBHOOK_API_KEY</span>) — verify it on every
          inbound call so only the connector can drive your CBS.
        </p>
      </section>

      {/* Pitfalls */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" /> Common pitfalls
        </h2>
        <div className="space-y-2">
          {PITFALLS.map(([title, desc]) => (
            <div key={title} className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm font-medium text-amber-900">{title}</p>
              <p className="text-sm text-amber-800 mt-0.5">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Reference */}
      <section>
        <div className="flex items-center justify-between mb-3 gap-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FileCode2 className="w-5 h-5 text-gray-400" /> The contract
          </h2>
          <div className="flex items-center gap-2">
            <button onClick={copyCode} disabled={!code}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
            <a href={REF_PATH} download
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700">
              <Download className="w-4 h-4" /> Download .java
            </a>
          </div>
        </div>
        <p className="text-sm text-gray-600 mb-3">
          <span className="font-mono text-xs">PayeeRequests.java</span> — the exact request/response
          DTOs for all four webhooks. Mirror these field names in your CBS handler.
        </p>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading contract…
          </div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
            Could not load contract: {error}
          </div>
        )}
        {!loading && !error && (
          <pre className="bg-gray-900 text-gray-100 text-xs rounded-lg p-4 overflow-auto max-h-[480px] leading-relaxed">
            {code}
          </pre>
        )}
      </section>
    </div>
  );
}
